<template>
  <div>
    <BaseHeader
      :title="$t('settings.title')"
      :style-variant="6"
      icon="mdi-cog"
    >
      <template
        v-if="!isPlatformBottimmo"
        #design
      >
        <HeaderDesign />
      </template>

      <template #desc>
        <p>{{ $t('settings.subtitle') }}</p>
      </template>
    </BaseHeader>

    <v-alert
      v-if="showRegeneration"
      border="start"
      type="warning"
      class="mb-4"
    >
      <v-row
        class="py-1 pl-4 pt-2"
        :class="{ 'pl-4': $vuetify.display.smAndDown }"
      >
        {{ $t('alerts.settings.show-regeneration') }}
      </v-row>
      <v-row
        class="justify-end"
        :class="{ 'justify-center': $vuetify.display.smAndDown }"
      >
        <Regenerate
          v-if="company"
          :company="company"
        />
      </v-row>
    </v-alert>

    <v-card
      border
      class="px-2 pt-2 px-md-4 v-card-wrapper"
    >
      <div>
        <v-tabs
          v-model="section"
          show-arrows
          height="40"
        >
          <v-tab
            v-for="{ name } in sections"
            :key="name"
            class="rounded-0"
          >
            {{ $t(name) }}
          </v-tab>
        </v-tabs>

        <v-divider />

        <v-window
          v-model="section"
          class="pt-4"
        >
          <v-window-item
            v-for="{ name, component } in sections"
            :key="name"
          >
            <component
              :is="getComponent(component)"
              :settings="settings"
              @refetch="handleRefetch"
            />
          </v-window-item>
        </v-window>
      </div>
    </v-card>
  </div>
</template>

<script>
import { markRaw } from 'vue'

import BaseHeader from '@/components/BaseHeader.vue'
import Permission from '@/components/Permission'
import featureNames from '@/lib/featureNames'
import brandingMixin from '@/mixins/branding.js'
import featureMixin from '@/mixins/feature'
import Regenerate from '@/modules/company/dialogs/Regenerate.vue'
import COMPANY from '@/modules/company/queries/Company.gql'
import PENDING_CHANGES_FOR_REGENERATION from '@/queries/PendingChangesForRegeneration.gql'

import AccountSettings from './accountSettings'
import CompanySettings from './companySettings'
import EmailSettings from './emailSettings'
import FunctionalSettings from './functionalSettings'
import HeaderDesign from './HeaderDesign.vue'
import PaymentSettings from './paymentSettings'
import ProfileSettings from './profileSettings'
import UserSettings from './userSettings'

// Mark all components as raw to prevent performance issues
const rawComponents = {
  FunctionalSettings: markRaw(FunctionalSettings),
  CompanySettings: markRaw(CompanySettings),
  AccountSettings: markRaw(AccountSettings),
  PaymentSettings: markRaw(PaymentSettings),
  EmailSettings: markRaw(EmailSettings),
  UserSettings: markRaw(UserSettings),
  ProfileSettings: markRaw(ProfileSettings),
}

export default {
  name: 'SettingsPage',

  components: {
    BaseHeader,
    HeaderDesign,
    Permission,
    Regenerate,
  },

  mixins: [featureMixin, brandingMixin],
  inject: ['role'],

  data() {
    return {
      section: null,
      pendingChangesForRegeneration: {},
      settings: {},
      sectionConfig: [
        {
          path: 'company',
          name: 'settings.company.name',
          permission: 'settings-company:read',
          component: rawComponents.CompanySettings,
        },
        {
          path: 'features',
          name: 'settings.features.name',
          permission: 'settings-features:read',
          component: rawComponents.FunctionalSettings,
        },
        {
          path: 'email',
          name: 'settings.email.name',
          permission: 'settings-email:read',
          feature: featureNames.HOPPERMATION,
          component: rawComponents.EmailSettings,
        },
        {
          path: 'user',
          name: 'settings.user.name',
          permission: 'settings-user:read',
          component: rawComponents.UserSettings,
        },
        {
          path: 'profile',
          name: 'settings.profile.name',
          permission: 'profile:read',
          component: rawComponents.ProfileSettings,
        },
        {
          path: 'payment',
          name: 'settings.payment.name',
          permission: 'settings-subscription:read',
          feature: featureNames.BILLWERK,
          component: rawComponents.PaymentSettings,
        },
        {
          path: 'contract',
          name: 'settings.contract.name',
          permission: 'settings-subscription:read',
          feature: featureNames.BILLWERK,
          component: rawComponents.AccountSettings,
        },
      ],
    }
  },

  computed: {
    sections() {
      return this.sectionConfig
        .filter(({ show }) => show === undefined || show())
        .filter(({ permission }) => this.role.isAuthorizedFor(permission))
        .filter(({ feature }) => feature === undefined || this.isFeatureActive(feature))
    },
    paths() {
      return this.sections.map((section) => section.path)
    },
    showRegeneration() {
      return (
        this.isFeatureActive(this.featureNames.REGENERATION) &&
        Object.keys(this.pendingChangesForRegeneration).length > 0
      )
    },
  },

  watch: {
    section(section) {
      if (this.paths[section] !== this.$route.params.section) {
        this.$router.replace('/settings/' + this.paths[section] || this.paths[0])
      }
    },
    $route() {
      const index = this.paths.indexOf(this.$route.params.section)
      this.section = index > 0 ? index : 0
    },
  },

  created() {
    const index = this.paths.indexOf(this.$route.params.section)
    this.section = index > 0 ? index : 0
  },

  methods: {
    getComponent(component) {
      return markRaw(component)
    },
    handleRefetch() {
      if (this.$apollo.queries.company) {
        this.$apollo.queries.company.refetch()
      }
    },
  },

  apollo: {
    company: {
      query: COMPANY,
      variables() {
        return {
          id: this.$auth.user.companyId,
        }
      },
    },
    pendingChangesForRegeneration: {
      query: PENDING_CHANGES_FOR_REGENERATION,
      variables() {
        return {
          companyId: this.$auth.user.companyId,
        }
      },
      skip() {
        return !this.role.isAuthorizedFor('settings-company:read')
      },
    },
  },
}
</script>
<style scoped>
.v-card-wrapper {
  margin-bottom: 100px;
}
.rounded-0 {
  border-radius: 0 !important;
}
</style>
